import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Plus, X, GripVertical, Info, RefreshCcw, Search, Target, Check, Sparkles, ArrowLeft } from "lucide-react";
import {
    DndContext,
    DragOverlay,
    useSensor,
    useSensors,
    PointerSensor,
    DragStartEvent,
    DragEndEvent,
    DragOverEvent,
    closestCenter,
    defaultDropAnimationSideEffects,
    DropAnimation,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ThesisPoint, MASTER_THESIS_POINTS, GREEN_FLAGS, RED_FLAGS, THESIS_CATEGORIES } from '@/constants/investorThesis';
import { useToast } from "@/hooks/use-toast";
import axios from 'axios';
import { getAuth } from "firebase/auth";
import { investorApi } from '@/services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Draggable Item for the Source List
function DraggableSourceItem({ item }: { item: ThesisPoint }) {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: item.id,
        data: {
            type: 'source',
            item,
        },
    });

    const style = {
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            style={style}
            className="group relative flex flex-col items-start gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 shadow-sm transition-all hover:bg-white dark:hover:bg-slate-800 hover:border-indigo-300 dark:hover:border-indigo-500 hover:shadow-md cursor-grab active:cursor-grabbing"
        >
            <div className="flex items-center gap-2 w-full">
                <div className={`p-1 rounded-lg shadow-sm group-hover:scale-110 transition-transform ${item.type === 'red'
                    ? 'bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400'
                    : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400'
                    }`}>
                    <Plus className="h-3.5 w-3.5" />
                </div>
                <h4 className="font-bold text-xs leading-tight text-slate-800 dark:text-white flex-1">{item.title}</h4>
            </div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed line-clamp-2 pl-1">
                {item.description}
            </p>
        </div>
    );
}

// Sortable Item for the Target List
function SortableTargetItem({ item, index, onRemove }: { item: ThesisPoint; index: number; onRemove: (id: string) => void }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: item.id,
        data: {
            type: 'target',
            item,
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="group relative flex items-center justify-between rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-white dark:bg-slate-800 p-2.5 shadow-sm hover:shadow-md transition-all hover:border-indigo-300 dark:hover:border-indigo-500/50"
        >
            <div className="flex items-center gap-3 flex-1 overflow-hidden" {...attributes} {...listeners}>
                <div className="cursor-grab text-slate-400 hover:text-indigo-500 transition-colors p-1">
                    <GripVertical className="h-4 w-4" />
                </div>
                <div className={`flex items-center justify-center h-6 w-6 rounded-full shrink-0 ${item.type === 'red'
                    ? 'bg-red-500 text-white'
                    : 'bg-emerald-500 text-white'
                    }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
                        <path d="M4 2a1 1 0 0 0-1 1v19a1 1 0 0 0 1 1h1V2H4Zm3 0v11l3.5-2.5L14 13V2H7Z" />
                    </svg>
                </div>
                <div className='flex flex-col min-w-0 flex-1 gap-0'>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {item.category === 'Custom' ? (
                            <span className="text-[9px] uppercase font-bold tracking-wider text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-1 py-0.5 rounded border border-emerald-100 dark:border-emerald-500/20 shrink-0">
                                Custom
                            </span>
                        ) : (
                            <span className="text-[9px] uppercase font-bold tracking-wider text-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 px-1 py-0.5 rounded border border-indigo-100 dark:border-indigo-500/20 shrink-0">
                                {item.category}
                            </span>
                        )}
                        <h4 className="font-bold text-xs truncate text-slate-900 dark:text-white">{item.title}</h4>
                    </div>
                    {item.description && (
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate w-full" title={item.description}>
                            {item.description}
                        </p>
                    )}
                </div>
            </div>
            <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                onClick={() => onRemove(item.id)}
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
}

export function ThesisPage() {
    const navigate = useNavigate();
    const [activeId, setActiveId] = useState<string | null>(null);
    const [activeItem, setActiveItem] = useState<ThesisPoint | null>(null);
    const [selectedPillars, setSelectedPillars] = useState<ThesisPoint[]>([]);

    const [customTitle, setCustomTitle] = useState("");
    const [customDesc, setCustomDesc] = useState("");

    const [searchQuery, setSearchQuery] = useState("");
    const [flagType, setFlagType] = useState<'green' | 'red'>('green');
    const { toast } = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [showGuide, setShowGuide] = useState(false);

    useEffect(() => {
        const hasSeenGuide = localStorage.getItem('hasSeenThesisGuide');
        if (!hasSeenGuide) {
            setShowGuide(true);
        }
    }, []);

    const handleCloseGuide = () => {
        localStorage.setItem('hasSeenThesisGuide', 'true');
        setShowGuide(false);
    };

    // Fetch existing thesis
    const { data: investorProfile, isLoading } = useQuery({
        queryKey: ['investor-profile'],
        queryFn: investorApi.getProfile,
        retry: false,
    });

    useEffect(() => {
        if (investorProfile && (investorProfile as any).thesis) {
            let initialData: ThesisPoint[] = [];
            const existingThesis = (investorProfile as any).thesis;

            if (Array.isArray(existingThesis)) {
                initialData = existingThesis;
            } else if (typeof existingThesis === 'string') {
                try {
                    initialData = JSON.parse(existingThesis);
                } catch (e) {
                    console.error("Failed to parse existing thesis:", e);
                    initialData = [];
                }
            }
            setSelectedPillars(initialData);
        }
    }, [investorProfile]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const currentSourcePool = flagType === 'green' ? GREEN_FLAGS : RED_FLAGS;

    const filteredMasterPoints = currentSourcePool.filter((point) => {
        if (selectedPillars.find((p) => p.id === point.id)) return false;
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            point.title.toLowerCase().includes(q) ||
            point.description.toLowerCase().includes(q) ||
            point.category.toLowerCase().includes(q)
        );
    });

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id as string);
        const item = active.data.current?.item as ThesisPoint;
        setActiveItem(item);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);
        setActiveItem(null);

        if (!over) return;

        if (active.data.current?.type === 'target' && over.data.current?.type === 'target') {
            if (active.id !== over.id) {
                setSelectedPillars((items) => {
                    const oldIndex = items.findIndex((i) => i.id === active.id);
                    const newIndex = items.findIndex((i) => i.id === over.id);
                    return arrayMove(items, oldIndex, newIndex);
                });
            }
        }
        else if (active.data.current?.type === 'source') {
            const item = active.data.current?.item as ThesisPoint;
            if (over.id === 'target-drop-zone' || over.data.current?.type === 'target') {
                if (!selectedPillars.find(p => p.id === item.id)) {
                    setSelectedPillars(prev => [...prev, item]);
                }
            }
        }
    };

    const handleDragOver = (event: DragOverEvent) => {
        // Optional visual feedback
    };

    const handleAddCustomPoint = () => {
        if (!customTitle.trim()) {
            toast({
                title: "Title Required",
                description: "Please enter a title for your custom thesis point.",
                variant: "destructive"
            });
            return;
        }

        const newPoint: ThesisPoint = {
            id: `custom-${Date.now()}`,
            title: customTitle,
            category: 'Custom',
            description: customDesc || 'Custom entry'
        };
        setSelectedPillars([...selectedPillars, newPoint]);
        setCustomTitle("");
        setCustomDesc("");
    };

    const handleRemovePoint = (id: string) => {
        setSelectedPillars(prev => prev.filter(p => p.id !== id));
    };

    const handleAddItem = (item: ThesisPoint) => {
        if (!selectedPillars.find(p => p.id === item.id)) {
            setSelectedPillars(prev => [...prev, item]);
        }
    };

    const queryClient = useQueryClient();

    const handleSave = async () => {
        try {
            setIsSaving(true);
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();

            await axios.patch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/investors/me/profile`, {
                thesis: selectedPillars
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast({
                title: "Thesis Saved",
                description: "Your investment thesis has been updated.",
            });
            await queryClient.invalidateQueries({ queryKey: ['investor-profile'] });
            navigate('/dashboard');
        } catch (error) {
            console.error(error);
            toast({
                title: "Error",
                description: "Failed to save thesis. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSkip = async () => {
        try {
            setIsSaving(true);
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();

            // Save a special item so it counts as "set"
            const skippedPoint: ThesisPoint = {
                id: 'skipped-setup',
                title: 'No specific thesis set',
                category: 'Custom',
                description: 'You skipped the initial setup. You can customize this anytime.'
            };

            await axios.patch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/investors/me/profile`, {
                thesis: [skippedPoint]
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast({
                title: "Setup Skipped",
                description: "You can set up your thesis later.",
            });
            await queryClient.invalidateQueries({ queryKey: ['investor-profile'] });
            navigate('/dashboard');
        } catch (error) {
            console.error(error);
            toast({
                title: "Error",
                description: "Failed to skip. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsSaving(false);
        }
    };

    const dropAnimation: DropAnimation = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    return (
        <div className="flex flex-col h-[calc(100vh-8rem)] gap-4 bg-slate-50 dark:bg-black overflow-hidden relative">
            <style>{`
                .glass-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
                }
                .dark .glass-card {
                    background: #0f172a;
                    border: 1px solid #1e293b;
                }
                .bg-logo-gradient {
                    background: linear-gradient(135deg, #3B52D0 0%, #A378CF 50%, #F0766E 100%);
                }
                .logo-gradient-text {
                    background: linear-gradient(to right, #3B52D0, #4194F2, #A378CF, #F0766E);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                /* Scrollbar Styling */
                ::-webkit-scrollbar { width: 6px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .dark ::-webkit-scrollbar-thumb { background: #334155; }
                .dark ::-webkit-scrollbar-thumb:hover { background: #475569; }
            `}</style>

            {/* Guide Modal */}
            <Dialog open={showGuide} onOpenChange={handleCloseGuide}>
                <DialogContent className="sm:max-w-[500px] border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-black font-display logo-gradient-text flex items-center gap-2">
                            <Sparkles className="w-6 h-6 text-indigo-500" />
                            Welcome to Your Thesis
                        </DialogTitle>
                        <DialogDescription className="text-base mt-2 text-slate-600 dark:text-slate-300">
                            Build your unique investment perspective by selecting traits you care about.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4 space-y-4">
                        <div className="flex gap-4 items-start">
                            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400 shrink-0">
                                <Plus className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white">1. Choose Flags</h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    Browse through carefully curated Green Flags (strengths) and Red Flags (risks) across different categories.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-4 items-start">
                            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg text-indigo-600 dark:text-indigo-400 shrink-0">
                                <GripVertical className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white">2. Drag & Drop</h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    Drag the concepts that matter most to you onto your "Thesis" canvas on the right, or simple click them to add.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-4 items-start">
                            <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg text-pink-600 dark:text-pink-400 shrink-0">
                                <Target className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white">3. Build Your DNA</h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    Rearrange them by priority. This forms your customized investment "DNA," which helps us tailor insights to your specific strategy.
                                </p>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="sm:justify-end border-t border-slate-100 dark:border-slate-800 pt-4 mt-4">
                        <Button
                            onClick={handleCloseGuide}
                            className="bg-logo-gradient text-white font-bold w-full sm:w-auto shadow-lg shadow-indigo-500/20 hover:opacity-90 transition-all"
                        >
                            Got it, let's start
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Header Section */}
            <div className="flex items-center gap-4 shrink-0 pb-1">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2 text-slate-500 shrink-0">
                    <ArrowLeft className="w-4 h-4" /> Back
                </Button>
                <div className="flex flex-col justify-center">
                    <h1 className="text-4xl font-black font-display tracking-tight leading-tight">
                        <span className="logo-gradient-text drop-shadow-sm">Design Your Thesis</span>
                    </h1>
                    <p className="text-slate-500 dark:text-slate-300 text-sm font-medium hidden md:block">
                        Drag concepts from the deck to map your unique strategy. This help to build your investment DNA for curated analysis.
                    </p>
                </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-6 relative z-10">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                >
                    {/* LEFT COLUMN: Exploration Deck */}
                    <div className="flex-1 min-w-0 flex flex-col rounded-2xl overflow-hidden glass-card shadow-sm">
                        <div className="bg-logo-gradient p-3 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-2">
                                <Sparkles className="text-white h-5 w-5" strokeWidth={2.5} />
                                <h2 className="text-white text-lg font-extrabold leading-tight font-display">Choose What Matters to You</h2>
                            </div>
                            <div className="relative w-48 hidden md:block">
                                <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                                    <Search className="h-3.5 w-3.5 text-white/70" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search traits..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full h-9 pl-9 pr-4 rounded-xl bg-white/20 border-none text-white placeholder:text-white/60 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                                />
                            </div>
                        </div>

                        {/* Green/Red Flag Toggle */}
                        <div className="px-5 py-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
                            <button
                                onClick={() => setFlagType('green')}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all border ${flagType === 'green'
                                    ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20'
                                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-300'
                                    }`}
                            >
                                <span className="text-sm">🟢</span> Green Flags
                            </button>
                            <button
                                onClick={() => setFlagType('red')}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all border ${flagType === 'red'
                                    ? 'bg-red-500 text-white border-red-500 shadow-lg shadow-red-500/20'
                                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-red-300'
                                    }`}
                            >
                                <span className="text-sm">🔴</span> Red Flags
                            </button>
                            <span className="text-[10px] text-slate-400 ml-2 hidden md:inline">
                                {flagType === 'green' ? 'Strengths you look for' : 'Risks to watch out for'}
                            </span>
                        </div>

                        <Tabs defaultValue={THESIS_CATEGORIES[0]} className="flex-1 flex flex-col min-h-0">
                            <div className="px-5 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
                                <ScrollArea className="w-full whitespace-nowrap" orientation="horizontal">
                                    <TabsList className="w-full justify-start h-auto flex-nowrap gap-2 bg-transparent p-0">
                                        {THESIS_CATEGORIES.map(cat => (
                                            <TabsTrigger
                                                key={cat}
                                                value={cat}
                                                className="rounded-full px-5 py-2 text-xs font-bold transition-all border border-slate-200 dark:border-slate-700 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4f46e5] data-[state=active]:to-[#818cf8] data-[state=active]:text-white data-[state=active]:border-transparent data-[state=active]:shadow-lg shadow-indigo-500/20 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                                            >
                                                {cat}
                                            </TabsTrigger>
                                        ))}
                                    </TabsList>
                                </ScrollArea>
                            </div>

                            <ScrollArea className="flex-1 p-3 bg-white dark:bg-slate-900">
                                {THESIS_CATEGORIES.map(cat => (
                                    <TabsContent key={cat} value={cat} className="mt-0 space-y-3 pb-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                            {filteredMasterPoints
                                                .filter(p => p.category === cat)
                                                .map(point => (
                                                    <div key={point.id} onClick={() => handleAddItem(point)}>
                                                        <DraggableSourceItem item={point} />
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        {filteredMasterPoints.filter(p => p.category === cat).length === 0 && (
                                            <div className="text-center py-20 flex flex-col items-center opacity-60">
                                                <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-full mb-4">
                                                    <RefreshCcw className="w-8 h-8 text-slate-400" />
                                                </div>
                                                <p className="text-slate-500 dark:text-slate-400 font-bold text-base">All items selected or no matches.</p>
                                            </div>
                                        )}
                                    </TabsContent>
                                ))}
                            </ScrollArea>
                        </Tabs>
                    </div>

                    {/* RIGHT COLUMN: Priority Stack */}
                    <div className="flex-1 md:max-w-[480px] lg:max-w-[520px] min-w-0 flex flex-col rounded-2xl overflow-hidden glass-card shadow-sm" id="target-drop-zone">
                        <div className="bg-logo-gradient p-3 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-2">
                                <Target className="text-white h-5 w-5" />
                                <h2 className="text-white text-lg font-extrabold leading-tight font-display">Your Thesis</h2>
                            </div>
                            <Badge className="bg-white/20 hover:bg-white/30 text-white border-none font-bold backdrop-blur-md px-2.5 py-0.5">
                                {selectedPillars.length} Rules
                            </Badge>
                        </div>

                        <ScrollArea className="flex-1 p-3 bg-slate-50 dark:bg-slate-900">
                            <SortableContext
                                items={selectedPillars.map(p => p.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="space-y-3 min-h-[100px] pb-4">
                                    {selectedPillars.length === 0 && (
                                        <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-2xl p-8 flex flex-col items-center justify-center text-center h-[350px] bg-white dark:bg-slate-800">
                                            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-full mb-4 animate-pulse">
                                                <Info className="h-8 w-8 text-indigo-400" />
                                            </div>
                                            <p className="font-bold text-slate-700 dark:text-slate-200 text-lg">Your Thesis Canvas</p>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-[280px] leading-relaxed">
                                                Drag investment pillars here to build your strategy.
                                            </p>
                                        </div>
                                    )}

                                    {selectedPillars.map((point, index) => (
                                        <SortableTargetItem
                                            key={point.id}
                                            item={point}
                                            index={index}
                                            onRemove={handleRemovePoint}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </ScrollArea>

                        {/* Custom Entry Panel */}
                        <div className="p-3 border-t border-indigo-100 dark:border-slate-700 bg-white dark:bg-slate-900">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="h-1 w-1 rounded-full bg-[#ec4899]"></div>
                                <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Add Custom Point</p>
                            </div>
                            <div className="space-y-2">
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Title"
                                        value={customTitle}
                                        onChange={(e) => setCustomTitle(e.target.value)}
                                        className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-indigo-500 h-8 font-medium shadow-sm text-sm"
                                    />
                                    <Button onClick={handleAddCustomPoint} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0 h-8 px-3 shadow-md shadow-indigo-500/20">
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                                <Input
                                    placeholder="Description (Optional)"
                                    value={customDesc}
                                    onChange={(e) => setCustomDesc(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddCustomPoint}
                                    className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:border-indigo-500 h-8 text-xs shadow-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <DragOverlay dropAnimation={dropAnimation}>
                        {activeId && activeItem ? (
                            <div className="flex items-center gap-3 rounded-xl border-2 border-indigo-500 bg-white dark:bg-slate-800 p-4 shadow-2xl opacity-90 w-[350px] scale-105 rotate-2 cursor-grabbing">
                                <span className="text-xl font-bold text-indigo-500 font-mono">#</span>
                                <div>
                                    <h4 className="font-bold text-sm text-slate-900 dark:text-white">{activeItem.title}</h4>
                                </div>
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between items-center shrink-0 pt-2">
                <div className="flex gap-3 ml-auto">
                    <Button variant="ghost" className="text-slate-500 font-bold hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800" onClick={handleSkip} disabled={isSaving}>
                        Skip for now
                    </Button>
                    <Button variant="outline" className="border-slate-300 dark:border-slate-600 font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => setSelectedPillars([])} disabled={selectedPillars.length === 0 || isSaving}>
                        Reset
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-logo-gradient text-white border-none font-bold shadow-xl shadow-indigo-500/20 px-8 hover:opacity-90 transition-all hover:-translate-y-0.5"
                    >
                        {isSaving ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                Saving...
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                Save Thesis <Check className="w-4 h-4" strokeWidth={3} />
                            </span>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
